// -----------------------------------------------------------------------------
// sessionSlice.js  (RAVEN standard Redux slice for user session management)
// -----------------------------------------------------------------------------
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import API from "../http-common";
import { debugLog, errorLog } from "../utils/debugUtils";
import { getRejectedMessage, toRejectedPayload } from "../utils/sliceErrorUtils";

// Fallback rehydration from sessionStorage.
// Primary hydration happens in store.js via preloadedState.
let persistedLoginUser = {};
let persistedIP = "";

try {
	persistedLoginUser = JSON.parse(sessionStorage.getItem("loginUser") || "{}");
	persistedIP = JSON.parse(sessionStorage.getItem("ip") || '""');
} catch {
	persistedLoginUser = {};
	persistedIP = "";
}

// ------------------------------- Initial State -------------------------------
const initialState = {
	currUser: persistedLoginUser || {},
	loginUser: persistedLoginUser || {},
	ip: persistedIP || "",
	status: {
		create: "idle",
		retrieve: "idle",
		retrieveOne: "idle",
		update: "idle",
		find: "idle",
	},
	error: {
		create: null,
		retrieve: null,
		retrieveOne: null,
		update: null,
		find: null,
	},
};

// ------------------------------- Async Thunks --------------------------------

/**
 * Fetch a user profile by email and set currUser/loginUser accordingly.
 * Mirrors previous loadUserProfile() logic.
 */
export const fetchUserProfile = createAsyncThunk(
	"session/fetchUserProfile",
	async ({ email, target = "currUser" }, { rejectWithValue }) => {
		try {
			const action = target === "loginUser" ? "login" : "find-by-email";
			const { data } = await API.get(`/users/${action}/${encodeURIComponent(email)}`);

			const roles = (data.user_accesses || []).map((x) => x.user_role);
			const userProfile = { ...data, user_roles: roles };

			debugLog(3, "[sessionSlice][FLOW] fetchUserProfile response", {
				email,
				target,
				module: "sessionSlice",
			});
			return { userProfile, target };
		} catch (err) {
			errorLog(4, "[sessionSlice][PROCESS] fetchUserProfile failed", {
				error: err,
				module: "sessionSlice",
			});
			// Preserve the backend's auth/profile message so startup/login flows can
			// distinguish a real API failure from a generic thunk error.
			return rejectWithValue(toRejectedPayload(err, "User profile fetch failed."));
		}
	}
);

// ------------------------------- Slice ---------------------------------------
const sessionSlice = createSlice({
	name: "session",
	initialState,
	reducers: {
		setCurrUser(state, action) {
			state.currUser = action.payload || {};
		},
		setLoginUser(state, action) {
			state.loginUser = action.payload || {};
		},
		setIP(state, action) {
			state.ip = action.payload || "";
		},
		clearSession(state) {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUserProfile.pending, (state) => {
				state.status.retrieve = "loading";
				state.error.retrieve = null;
			})
			.addCase(fetchUserProfile.fulfilled, (state, action) => {
				state.status.retrieve = "succeeded";
				const { userProfile, target } = action.payload;
				if (target === "loginUser") state.loginUser = userProfile;
				else state.currUser = userProfile;
			})
			.addCase(fetchUserProfile.rejected, (state, action) => {
				state.status.retrieve = "failed";
				state.error.retrieve = getRejectedMessage(action);
			});
	},
});

export const { setCurrUser, setLoginUser, setIP, clearSession } = sessionSlice.actions;
export default sessionSlice.reducer;
